import { flatten, partition, uniq, without } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  Bearing,
  CalculatedLine,
  Component,
  ComponentDesign,
  FeaturePatternTrussContainer,
  isTruss,
  JackGirderConnection,
  Member,
  MemberLine,
  RelativeGeometry,
  RelativeMemberGeometry,
  RoofContainer,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  allComponentDesignsUnloaded,
  componentDesignsDeleted,
  getInsertionsWithPendingChanges,
  getLayoutModelsWithPendingChanges,
  insertionSetQuantity,
  saveAllProjectChanges,
} from '@paragon/layout-models';
import { hasProjectHierarchyFeatureFlag } from '@paragon/login-tools';
import { enqueueError, enqueueInfo, enqueueWarning } from '@paragon/notification-tools';
import { getAllRelativeGeometries, isExplicitCalculatedLine } from '@paragon/relative-geometry';
import { addSpinnerMessage, areAnySpinnersActive, removeSpinnerMessage } from '@paragon/task-spinner';
import { calculateBearingGeometry, calculateMemberPolygon } from '@paragon/three-shared';
import {
  forceArray,
  formatAsNaturalLanguageList,
  groupByWithoutNullishKeys,
  naturalSortByProperty,
  notNullish,
} from '@paragon/utilities';

import { pickSingleActiveBuildingGuid } from '../../library/hierarchy-filtering/active-building';
import { pickSingleActiveLevelGuid } from '../../library/hierarchy-filtering/active-level';
import {
  getAllDependenciesOfCalculatedLine,
  getAllDependentLinesOfCalculatedLine,
  getAllReferencedLineGuidsForRelativeGeometry,
  getDirectDependencyGuidsForNonMemberReferenceLine,
} from '../../library/relativeGeometry/getAllDependentLinesOfCalculatedLines';
import { hasInvalidRelativeGeometry } from '../../library/relativeGeometry/hasInvalidRelativeGeometry';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import {
  isConsideredLayoutItem,
  isEntry,
  SelectableEntryOfType,
  SelectableItemEntry,
  SelectableItemType,
  SelectedItemIdentifier,
} from '../../library/selection/types';
import { getBearingEnvelopeUserVisibleTerm } from '../../library/utilities/bearingEnvelopeUserTerm';
import { RootState } from '..';
import { createRelativeComponentDesignSelector } from '../selectors/componentNavigationSelectors';
import { getActiveComponentDesign, getActiveComponentDesignGuid } from '../selectors/getActiveComponentDesignSelector';
import {
  ADDITIONAL_LOAD_PROPERTIES,
  getSelectedEntryFactory,
  SelectedEntryFactory,
} from '../selectors/getSelectedItemSelector';
import {
  getAllBuildingGuids,
  getAllLevelGuids,
  getLayoutModelOccupancyReport,
  getLevelGuidsAffectedByWorkLineEdit,
  getVisibleBuildingGuidOrNull,
  LayoutModelOccupancyReport,
} from '../selectors/layoutDataSelectors';
import { isLayoutModeViewOnly, isLayoutViewActive } from '../selectors/layoutModeSelectors';
import { isPickingActive } from '../selectors/pickingSelectors';
import { ClientContext } from '../slices/applicationSlice';
import { setAdditionalLoads, setAnalogModel, setComponent, setPlatePairs } from '../slices/componentDesignsSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { removeJackGirderConnections } from '../slices/jackGirderConnectionsSlice';
import { layoutSlice } from '../slices/layoutSlice';
import { clearActiveComponentDesignGuid, ghostObjectVisibilityEnabled } from '../slices/selectionsSlice';
import { trussGroupsDeleted } from '../slices/trussGroupsSlice';
import { clearRightClickedItems } from '../slices/viewportSlice';
import { setUnsavedComponentDesignFromServer } from './appActions';
import { saveBearingEnvelopes } from './layout/bearingEnvelopeActions';
import { saveCeilingPlanes } from './layout/ceilingPlaneActions';
import { saveCeilingTrays } from './layout/ceilingTrayActions';
import {
  disassociateFeaturePatternContainersFromBearings,
  saveFeaturePatternContainers,
} from './layout/featurePatternContainerActions';
import { disassociatePlanesFromBearings } from './layout/genericPlaneActions';
import { saveRoofPlanes } from './layout/roofPlaneActions';
import { saveSnappingUnderlays } from './layout/snappingUnderlayActions';
import { saveTrussEnvelopes } from './layout/trussEnvelopeActions';
import { saveWorkLines } from './layout/workLineActions';
import { ensureLevelIsVisible, hideLevelByGuid } from './levelVisibilityActions';
import { deleteHangers, generateLoadCasesOfActiveComponentDesign } from './paragonProcessActions';
import {
  showDeleteCarriedBearingConfirmationDialog,
  showDeleteComponentConfirmationDialog,
  showDeleteRoofContainerConfirmationDialog,
  showDeleteTrussGroupsConfirmationDialog,
} from './popupActions';
import { deleteFixedWebJoint, deleteWebSegmentsAndRewebTruss } from './profileActions';
import { receiveActiveProjectSynchronizationPayload, saveAllProjectChangesWithSpinnerMessage } from './projectActions';
import { forceClearSelection, resetSelectedItems, selectEntries, selectItem } from './selectActions';
import { AsyncThunkActionVoid, ThunkActionReturn, ThunkActionVoid } from './thunkActions';
import { updateTrussGroup } from './trussGroups';
import { updateUrlFromState } from './urlActions';
import { cycleVisibilityOfItem } from './visualOptionActions';

type DeletionAction = ThunkActionReturn<Promise<void> | void>;

type DeletionActionCreator<T extends SelectableItemType> = (requirements: RequirementsProvider<T>) => DeletionAction;

const deletionActionCreators: { [T in SelectableItemType]?: DeletionActionCreator<T> } = {};

//////////////////////////////////////////////////////////
// Universal deletion actions (use these when possible) //
//////////////////////////////////////////////////////////

export const deleteItems =
  (
    items: SelectedItemIdentifier | SelectableItemEntry | (SelectedItemIdentifier | SelectableItemEntry)[],
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const itemsArray = forceArray(items);

    if (itemsArray.length === 0) return;

    const factory = new DeletionActionFactory(itemsArray, getDeletionProps(getState()));
    const deletionFeasibility = factory.getDeletionFeasibility();
    if (deletionFeasibility.deletionFeasible) {
      await dispatch(factory.getDeletionAction());
    } else {
      dispatch(enqueueError(deletionFeasibility.failureReason));
    }
  };

export const deleteAllSelectedEntries = (): AsyncThunkActionVoid => async (dispatch, getState) =>
  dispatch(deleteItems(getSelectedItemIdentifiers(getState())));

//////////////////////////////////
// Specialized deletion actions //
//////////////////////////////////

const deselectEntriesBeingDeletedIfNecessary =
  (entries: SelectableItemEntry | readonly SelectableItemEntry[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const selectedItems = getSelectedItemIdentifiers(state);

    const selectedEntriesBeingDeleted = forceArray(entries).filter((entry) =>
      selectedItems.some((selectedItem) => entry.item.guid === selectedItem.guid),
    );

    if (selectedEntriesBeingDeleted.length > 0) {
      dispatch(selectEntries(selectedEntriesBeingDeleted, { intent: 'deselect' }));
    }
  };

export const deleteAllCarriedComponentConnectionsOnActiveComponent =
  (): AsyncThunkActionVoid => async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Deleting carried component connections...'));

    try {
      const carryingComponentDesignGuid = getActiveComponentDesignGuid(getState());
      if (!carryingComponentDesignGuid) throw new Error('No active component');
      const { removedGuids, updatedTimestamps } =
        await designService.jackGirderConnections.removeAllFromGirder(carryingComponentDesignGuid);
      dispatch(setComponentDesignTimestamps({ timestamps: updatedTimestamps }));
      dispatch(removeJackGirderConnections(removedGuids));
    } catch (exception) {
      console.error(exception);
      dispatch(
        enqueueError('Unable to delete carried component connections.  Please contact Paragon Customer Support.'),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const deleteAllSplices = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const spinnerId = dispatch(addSpinnerMessage('Deleting splices...'));

  try {
    const componentDesign = getActiveComponentDesign(getState());
    if (!componentDesign) throw new Error('No active component');
    const newComponentDesign = await designService.componentDesigns.postDeleteAllSplices(componentDesign);
    dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
  } catch (exception) {
    console.error(exception);
    dispatch(enqueueError('Unable to delete splices.  Please contact Paragon Customer Support.'));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

// Note that this action has only been tested for use in a station context. Use caution if adopting it elsewhere.
export const deleteAllComponentDesigns = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  if (state.application.openContext?.type === 'station') {
    const spinnerId = dispatch(addSpinnerMessage('Clearing station...'));

    try {
      await designService.stations.clearStation(state.application.openContext.stationGuid);
    } catch (err) {
      console.error(err);
      dispatch(enqueueError('Error clearing station.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  }

  dispatch(allComponentDesignsUnloaded());
  dispatch(forceClearSelection());

  dispatch(updateUrlFromState());
};

export const deleteAllPlatePairs = (): ThunkActionVoid => (dispatch, getState) => {
  const componentDesign = getActiveComponentDesign(getState());
  if (!componentDesign) return;
  dispatch(setPlatePairs({ platePairs: [], componentDesignGuid: componentDesign.guid }));
};

export const deleteRoofContainerAndAssociatedTrussEnvelopes =
  (projectGuid: string, roofContainer: RoofContainer): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Deleting roof container...'));

    try {
      await designService.projects.deleteRoofContainer(projectGuid, roofContainer.guid);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Unable to delete roof container. Try again, or contact Paragon Customer Support.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(resetSelectedItems());
    dispatch(layoutSlice.actions.roofContainerDeleted(roofContainer.guid));
  };

export const deleteFeaturePatternContainersAndAssociatedTrussEnvelopes =
  (projectGuid: string, featurePatternContainers: readonly FeaturePatternTrussContainer[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Deleting roof container...'));
    const name = featurePatternContainers.length === 1 ? 'feature pattern container' : 'feature pattern containers';
    const guids = featurePatternContainers.map(({ guid }) => guid);

    try {
      await designService.projects.deleteFeaturePatternContainers(projectGuid, guids);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`Unable to delete ${name}. Try again, or contact Paragon Customer Support.`));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(resetSelectedItems());
    dispatch(layoutSlice.actions.featurePatternContainersDeleted(guids));
  };

const deleteTrussEnvelopes =
  (
    envelopes: readonly SelectableEntryOfType<'trussEnvelope'>[],
    projectGuid: string,
    { withSpinnerMessage }: { withSpinnerMessage: boolean },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = withSpinnerMessage ? dispatch(addSpinnerMessage('Deleting truss envelope...')) : null;

    const trussEnvelopeGuids = envelopes.map((entry) => entry.item.guid);

    // Delete from any Truss Groups by which they are referenced
    const trussGroups = getState().trussGroups;
    const touchedTrussGroupNames: string[] = [];
    for (const trussGroup of Object.values(trussGroups)) {
      const trussEnvelopeGuidsWithoutDeletedOnes = without(trussGroup.trussEnvelopeGuids, ...trussEnvelopeGuids);
      if (trussEnvelopeGuidsWithoutDeletedOnes.length !== trussGroup.trussEnvelopeGuids.length) {
        await dispatch(
          updateTrussGroup({
            ...trussGroup,
            trussEnvelopeGuids: trussEnvelopeGuidsWithoutDeletedOnes,
          }),
        );
        touchedTrussGroupNames.push(trussGroup.name);
      }
    }

    if (touchedTrussGroupNames.length > 0) {
      dispatch(
        enqueueInfo(
          `Deleted Truss Envelope${trussEnvelopeGuids.length > 1 ? 's' : ''} also removed from Truss Group${touchedTrussGroupNames.length > 1 ? 's:' : ''} ${formatAsNaturalLanguageList(
            naturalSortByProperty(touchedTrussGroupNames, (name) => name),
            'and',
          )}`,
        ),
      );
    }

    let syncPayload;

    try {
      syncPayload = await designService.projects.deleteTrussEnvelopes(projectGuid, trussEnvelopeGuids);
    } catch (error) {
      console.error(error);

      dispatch(enqueueError('Unable to delete truss envelope. Try again, or contact Paragon Customer Support.'));
      return;
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(deselectEntriesBeingDeletedIfNecessary(envelopes));
    dispatch(receiveActiveProjectSynchronizationPayload(syncPayload));
    dispatch(layoutSlice.actions.trussEnvelopesDeleted(trussEnvelopeGuids));

    return;
  };

/**
 * Exported for use by a confirmation dialog. You should prefer one of the universal actions when initiating deletion.
 */
export const deleteTrussGroups =
  (projectGuid: string, trussGroupGuids: readonly string[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const trussGroups = getState().trussGroups;

    const spinnerId = dispatch(addSpinnerMessage(`Deleting truss group${trussGroupGuids.length > 1 ? 's' : ''}...`));

    try {
      dispatch(
        deselectEntriesBeingDeletedIfNecessary(
          trussGroupGuids.map((guid) => ({ type: 'trussGroup', item: trussGroups[guid] })),
        ),
      );
      await designService.projects.deleteTrussGroups(projectGuid, trussGroupGuids);
      dispatch(trussGroupsDeleted(trussGroupGuids));
    } catch (error) {
      console.error(error);

      dispatch(
        enqueueError(
          `Unable to delete truss group${trussGroupGuids.length > 1 ? 's' : ''}. Try again, or contact Paragon Customer Support.`,
        ),
      );
      return;
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };

/**
 * Exported for use by a confirmation dialog. You should prefer one of the universal actions when initiating deletion.
 */
export const deleteBearing =
  (componentDesign: ComponentDesign, bearing: Bearing): AsyncThunkActionVoid =>
  async (dispatch) => {
    if (!componentDesign) return;

    let newComponentDesign: ComponentDesign;

    const isInvalidGeometry =
      bearing.relativeGeometry && calculateBearingGeometry(bearing, componentDesign.component) === null;
    if (isInvalidGeometry) {
      const result = deleteBearingWithInvalidGeometry(componentDesign.component, bearing);
      if (result.error) {
        dispatch(enqueueWarning(result.message));
        return;
      } else {
        newComponentDesign = { ...componentDesign, component: result.component };
      }
    } else {
      const spinnerId = dispatch(addSpinnerMessage('Deleting bearing...'));
      try {
        newComponentDesign = await designService.componentDesigns.postDeleteBearing(componentDesign, bearing.guid);
      } catch {
        dispatch(enqueueError('Unable to delete bearing. Please contact Paragon Customer Support.'));
        return;
      } finally {
        dispatch(removeSpinnerMessage(spinnerId));
      }
    }

    dispatch(deselectEntriesBeingDeletedIfNecessary({ type: 'bearing', item: bearing }));
    dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
  };

function deleteBearingWithInvalidGeometry(
  component: Component,
  bearing: Bearing,
): { error: true; message: string } | { error: false; component: Component } {
  const { relativeGeometry } = bearing;
  if (!relativeGeometry) throw new Error('Expected bearing to have relative geometry.');

  const dependenciesResult = relativeGeometryHasDependents(relativeGeometry.guid, bearing.name, component);
  if (dependenciesResult.hasDependents) {
    return { error: true, message: dependenciesResult.dependentsMessage };
  }

  return {
    error: false,
    component: {
      ...component,
      bearings: component.bearings.filter((b) => b.guid !== bearing.guid),
      referenceLines:
        component.referenceLines?.filter(
          (referenceLine) => !dependenciesResult.referenceLineGuidsToDelete.includes(referenceLine.guid),
        ) ?? null,
    },
  };
}

// Exported for use by a confirmation dialog. You should prefer one of the universal actions when initiating deletion.
export const deleteComponentDesignsWithoutConfirmation =
  (targetGuidOrArray: string | string[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const targetedGuids = new Set(forceArray(targetGuidOrArray));

    const spinnerId = dispatch(addSpinnerMessage(`Deleting component design${targetedGuids.size > 1 ? 's' : ''}...`));

    try {
      const stateAtConfirmation = getState();
      let componentDesignToSelect;
      const activeComponentDesignGuid = getActiveComponentDesignGuid(stateAtConfirmation);
      if (activeComponentDesignGuid && targetedGuids.has(activeComponentDesignGuid)) {
        componentDesignToSelect =
          createRelativeComponentDesignSelector(1, targetedGuids)(stateAtConfirmation) ??
          createRelativeComponentDesignSelector(-1, targetedGuids)(stateAtConfirmation);
      }

      const componentDesignEntriesBeingDeleted = Array.from(targetedGuids)
        .map((guid) => stateAtConfirmation.componentDesigns.data[guid])
        .filter(notNullish)
        .map((componentDesign) => ({ type: 'componentDesign' as const, item: componentDesign }));

      let componentDesignCleared = false;
      if (isLayoutViewActive(stateAtConfirmation)) {
        // In layout mode, we don't want to select a new component design as that would force a mode change. Just clear the
        //   active design guid instead.
        dispatch(clearActiveComponentDesignGuid());
        dispatch(deselectEntriesBeingDeletedIfNecessary(componentDesignEntriesBeingDeleted));
        componentDesignCleared = true;
      } else if (componentDesignToSelect != null) {
        // In design mode, we're fine to (and actually want to) just select a different component design instead. We've
        //   already picked out the right one above. Selecting is exclusive, so this clears them in the same transition.
        dispatch(selectItem(componentDesignToSelect, 'componentDesign'));
      } else {
        dispatch(deselectEntriesBeingDeletedIfNecessary(componentDesignEntriesBeingDeleted));
      }

      let state = getState();

      if (state.application.openContext?.type === 'station') {
        try {
          await designService.stations.removeComponentDesignGuidsFromStation(
            state.application.openContext.stationGuid,
            Array.from(targetedGuids),
          );
        } catch (err) {
          console.error(err);
          dispatch(enqueueError('Error deleting component.'));
          return;
        }
      }

      const affectedJackGirderConnections = Object.values(state.jackGirderConnections.data).filter(
        (connection) => targetedGuids.has(connection.jackGuid) || targetedGuids.has(connection.girderGuid),
      );

      await dispatch(deleteJackGirderConnections(affectedJackGirderConnections));

      if (state.application.openContext?.type === 'project') {
        const updatedTrussEnvelopes = Object.values(state.layout.trussEnvelopes)
          .filter(
            (trussEnvelope) =>
              trussEnvelope.componentDesignGuid !== null && targetedGuids.has(trussEnvelope.componentDesignGuid),
          )
          .map((trussEnvelope) => ({ ...trussEnvelope, componentDesignGuid: null }));

        if (updatedTrussEnvelopes.length !== 0) {
          const syncPayload = await designService.projects.upsertTrussEnvelopes(
            state.application.openContext.projectGuid,
            updatedTrussEnvelopes,
          );

          dispatch(receiveActiveProjectSynchronizationPayload(syncPayload));
          dispatch(layoutSlice.actions.trussEnvelopesUpserted(updatedTrussEnvelopes));
        }
      }

      dispatch(componentDesignsDeleted(targetedGuids));

      if (state.application.openContext?.type === 'project') {
        await dispatch(saveAllProjectChangesWithSpinnerMessage('deleting component design(s)'));
      }

      state = getState();

      // If every component design was deleted, we need to reset the URL
      const allComponentDesignsDeleted = Object.keys(state.componentDesigns.data).length === 0;
      const urlNeedsUpdate = componentDesignCleared || allComponentDesignsDeleted;
      if (urlNeedsUpdate) dispatch(updateUrlFromState());
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

// Exported for use by a confirmation dialog. You should prefer one of the universal actions when initiating deletion.
export const deleteJackGirderConnections =
  (targetOrArray: JackGirderConnection | JackGirderConnection[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    const jackGirderConnections = forceArray(targetOrArray);

    if (jackGirderConnections.length === 0) return;

    const spinnerId = dispatch(addSpinnerMessage('Deleting carried component connection(s)...'));

    dispatch(
      deselectEntriesBeingDeletedIfNecessary(
        jackGirderConnections.map((item) => ({ type: 'jackGirderConnection', item })),
      ),
    );

    try {
      const guids = jackGirderConnections.map((connection) => connection.guid);
      const timestamps = await designService.jackGirderConnections.remove(guids);

      dispatch(setComponentDesignTimestamps({ timestamps }));
      dispatch(removeJackGirderConnections(guids));
    } catch (exception) {
      console.error(exception);
      dispatch(
        enqueueError('Unable to delete carried component connection.  Please contact Paragon Customer Support.'),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

function categorizeMembersForDeletion(members: Member[], component: Component) {
  const result = {
    membersWithoutRelativeGeometry: [] as Member[],
    membersWithInvalidGeometry: [] as Member[],
    normalValidMembers: [] as Member[],
  };

  for (const member of members) {
    if (member.relativeGeometry == null) {
      result.membersWithoutRelativeGeometry.push(member);
    } else if (calculateMemberPolygon(member, component) == null) {
      result.membersWithInvalidGeometry.push(member);
    } else {
      result.normalValidMembers.push(member);
    }
  }

  return result;
}

// Broken out into a separate action for use by both members and braces.
const deleteMembers =
  (componentDesign: ComponentDesign, members: Member[], options: { isBrace: boolean }): AsyncThunkActionVoid =>
  async (dispatch) => {
    let { component } = componentDesign;

    // If any of the members or bearings do not form a valid geometry, then the server will not be able to convert the
    // component to a calculation component in order to delete the member. Handle those before proceeding.
    const { membersWithoutRelativeGeometry, membersWithInvalidGeometry, normalValidMembers } =
      categorizeMembersForDeletion(members, component);

    for (const member of membersWithoutRelativeGeometry) {
      component = deleteMemberWithNoRelativeGeometry(component, member, options);
    }

    for (const member of membersWithInvalidGeometry) {
      const result = deleteMemberWithInvalidGeometry(component, member, options);

      if (result.error) {
        dispatch(enqueueWarning(result.message));
      } else {
        component = result.component;
      }
    }

    const namesOfInvalidMembers = component.members
      .filter((member) => !calculateMemberPolygon(member, component))
      .map((member) => member.name);
    const namesOfInvalidBraces = (isTruss(component) ? component.braceMembers : [])
      .filter((member) => !calculateMemberPolygon(member, component))
      .map((member) => member.name);
    const namesOfInvalidBearings = component.bearings
      .filter((bearing) => !calculateBearingGeometry(bearing, component))
      .map((bearing) => bearing.name);

    if (namesOfInvalidMembers.length > 0 || namesOfInvalidBraces.length > 0 || namesOfInvalidBearings.length > 0) {
      const invalidGeometryIdentifiers = [];

      if (namesOfInvalidMembers.length > 0) {
        invalidGeometryIdentifiers.push(`Member(s) ${formatAsNaturalLanguageList(namesOfInvalidMembers, 'and')}`);
      }

      if (namesOfInvalidBraces.length > 0) {
        invalidGeometryIdentifiers.push(`Brace(s) ${formatAsNaturalLanguageList(namesOfInvalidBraces, 'and')}`);
      }

      if (namesOfInvalidBearings.length > 0) {
        invalidGeometryIdentifiers.push(`Bearing(s) ${formatAsNaturalLanguageList(namesOfInvalidBearings, 'and')}`);
      }

      const errorMessage = `${formatAsNaturalLanguageList(
        invalidGeometryIdentifiers,
        'and',
      )} have invalid geometry. Either fix their geometry or delete them before proceeding.`;
      dispatch(enqueueError(errorMessage));

      return;
    }

    const spinnerId = dispatch(
      addSpinnerMessage(`Deleting ${normalValidMembers.length > 1 ? 'members' : 'member'}...`),
    );

    try {
      const updatedComponentDesign = await designService.componentDesigns.postDeleteMembers(
        { ...componentDesign, component },
        normalValidMembers.map((member) => member.guid),
      );

      dispatch(
        deselectEntriesBeingDeletedIfNecessary(
          members.map((item) => ({ type: options?.isBrace ? 'brace' : 'member', item })),
        ),
      );
      dispatch(setUnsavedComponentDesignFromServer(updatedComponentDesign));
    } catch (error) {
      dispatch(enqueueError('Unable to delete member. Please contact Paragon Customer Support.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

function deleteMemberWithNoRelativeGeometry(
  component: Component,
  member: Member,
  options: { isBrace: boolean },
): Component {
  if (options.isBrace) {
    if (!isTruss(component)) throw new Error('Cannot delete brace on a non-truss.');

    return {
      ...component,
      braceMembers: component.braceMembers.filter((m) => m.guid !== member.guid),
    };
  } else {
    return {
      ...component,
      members: component.members.filter((m) => m.guid !== member.guid),
    };
  }
}

function deleteMemberWithInvalidGeometry(
  component: Component,
  member: Member,
  options: { isBrace: boolean },
): { error: true; message: string } | { error: false; component: Component } {
  const { relativeGeometry } = member;
  if (!relativeGeometry) throw new Error('Expected member to have relative geometry.');

  const dependenciesResult = relativeGeometryHasDependents(relativeGeometry.guid, member.name, component);
  if (dependenciesResult.hasDependents) {
    return { error: true, message: dependenciesResult.dependentsMessage };
  }

  if (!options.isBrace) {
    return {
      error: false,
      component: {
        ...component,
        members: component.members.filter((m) => m.guid !== member.guid),
        referenceLines:
          component.referenceLines?.filter(
            (referenceLine) => !dependenciesResult.referenceLineGuidsToDelete.includes(referenceLine.guid),
          ) ?? null,
      },
    };
  } else {
    if (!isTruss(component)) throw new Error('Tried to delete brace on non-truss.');

    return {
      error: false,
      component: {
        ...component,
        braceMembers: component.braceMembers.filter((braceMember) => braceMember.guid !== member.guid),
        referenceLines:
          component.referenceLines?.filter(
            (referenceLine) => !dependenciesResult.referenceLineGuidsToDelete.includes(referenceLine.guid),
          ) ?? null,
      },
    };
  }
}

// Checks if a CalculatedLine is referenced by a RelativeMemberGeometry.
const references = (referenceLine: CalculatedLine) => (geometry: RelativeMemberGeometry) =>
  flatten([geometry.lineGuid, geometry.baseCutGuids, geometry.endCutGuids]).includes(referenceLine.guid);

function relativeGeometryHasDependents(
  relativeGeometryGuid: string,
  objectName: string,
  component: Component,
):
  | {
      readonly hasDependents: true;
      readonly dependentsMessage: string;
      readonly referenceLineGuidsToDelete: readonly string[];
    }
  | { readonly hasDependents: false; readonly referenceLineGuidsToDelete: readonly string[] } {
  const memberLineGuidsForRelativeGeometry = (component.referenceLines ?? [])
    .filter((line) => line.lineType === 'Member' && line.memberGeometryGuid === relativeGeometryGuid)
    .map((line) => line.guid);

  const namesOfDependentReferenceLines = (component.referenceLines ?? [])
    .filter(isExplicitCalculatedLine)
    .filter((line) =>
      getDirectDependencyGuidsForNonMemberReferenceLine(line).some(
        (lineGuid) => lineGuid != null && memberLineGuidsForRelativeGeometry.includes(lineGuid),
      ),
    )
    .map((line) => line.name);

  const objectsRelativeGeometryReferencesLine = (object: { readonly relativeGeometry: RelativeGeometry | null }) =>
    object.relativeGeometry &&
    getAllReferencedLineGuidsForRelativeGeometry(object.relativeGeometry).some(
      (lineGuid) => lineGuid != null && memberLineGuidsForRelativeGeometry.includes(lineGuid),
    );

  const namesOfDependentMembers = component.members
    .filter(objectsRelativeGeometryReferencesLine)
    .map((member) => member.name);
  const namesOfDependentBraces = (isTruss(component) ? component.braceMembers : [])
    .filter(objectsRelativeGeometryReferencesLine)
    .map((member) => member.name);
  const namesOfDependentBearings = component.bearings
    .filter(objectsRelativeGeometryReferencesLine)
    .map((bearing) => bearing.name);

  if (
    namesOfDependentReferenceLines.length > 0 ||
    namesOfDependentMembers.length > 0 ||
    namesOfDependentBraces.length > 0 ||
    namesOfDependentBearings.length > 0
  ) {
    const dependentGeometryIdentifiers = [];

    if (namesOfDependentReferenceLines.length > 0) {
      dependentGeometryIdentifiers.push(
        `Reference Line(s) ${formatAsNaturalLanguageList(namesOfDependentReferenceLines, 'and')}`,
      );
    }

    if (namesOfDependentMembers.length > 0) {
      dependentGeometryIdentifiers.push(`Member(s) ${formatAsNaturalLanguageList(namesOfDependentMembers, 'and')}`);
    }

    if (namesOfDependentBraces.length > 0) {
      dependentGeometryIdentifiers.push(`Brace(s) ${formatAsNaturalLanguageList(namesOfDependentBraces, 'and')}`);
    }

    if (namesOfDependentBearings.length > 0) {
      dependentGeometryIdentifiers.push(`Bearing(s) ${formatAsNaturalLanguageList(namesOfDependentBearings, 'and')}`);
    }

    return {
      hasDependents: true,
      dependentsMessage: `${objectName} cannot be deleted because its geometry is invalid and ${formatAsNaturalLanguageList(
        dependentGeometryIdentifiers,
        'and',
      )} depend on it. Either fix ${objectName}'s geometry or delete the dependent objects before proceeding.`,
      referenceLineGuidsToDelete: memberLineGuidsForRelativeGeometry,
    };
  }

  return { hasDependents: false, referenceLineGuidsToDelete: memberLineGuidsForRelativeGeometry };
}

export const standardReferenceLineKeys = [
  'verticalZeroGuid',
  'verticalMaxGuid',
  'horizontalZeroGuid',
  'horizontalMaxGuid',
  'leftCutBackTopChordGuid',
  'leftCutBackBottomChordGuid',
  'rightCutBackTopChordGuid',
  'rightCutBackBottomChordGuid',
  'leftInnerMostEndConditionVerticalGuid',
  'rightInnerMostEndConditionVerticalGuid',
  'overhangLeftGuid',
  'overhangRightGuid',
  'seatCutLeftGuid',
  'seatCutRightGuid',
  'overhangOneGuid',
  'overhangTwoGuid',
] as const;

const deleteReferenceLine =
  (referenceLine: CalculatedLine): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const componentDesign = getActiveComponentDesign(getState());
    const component = componentDesign?.component;
    if (!componentDesign || !component || !component.referenceLines) {
      console.error('Invalid state—unable to delete reference line.');
      return;
    }

    if (standardReferenceLineKeys.some((key) => component[key] === referenceLine.guid)) {
      dispatch(enqueueError('Cannot delete standard reference line.'));
      return;
    }

    const memberGeometries = component.members.map((member) => member.relativeGeometry).filter(notNullish);
    const geometries = getAllRelativeGeometries(component);
    const dependentLines = getAllDependentLinesOfCalculatedLine(referenceLine, component.referenceLines, geometries);

    const failBecause = (reason: string) => dispatch(enqueueError(`Reference line can't be deleted because ${reason}`));

    if (dependentLines.length > 0) {
      const [memberLines, otherLines] = partition(
        dependentLines,
        (line): line is MemberLine => line.lineType === 'Member',
      );
      const memberLineNames = uniq(
        memberLines.map(
          // 'unknown' should never be triggered, but we don't want to crash if it is.
          (line) => memberGeometries.find((member) => member.guid === line.memberGeometryGuid)?.memberName ?? 'unknown',
        ),
      );
      const otherLineNames = otherLines.map((line) => line.name).filter((name) => !memberLineNames.includes(name));

      const nameCount = memberLineNames.length + otherLineNames.length;
      if (nameCount > 10) {
        failBecause(`${nameCount} other lines or members depend on it.`);
        return;
      }

      let dependentNames = '';
      if (otherLineNames.length > 0) {
        dependentNames += `line${otherLineNames.length > 1 ? 's' : ''} ${formatAsNaturalLanguageList(
          otherLineNames,
          'and',
        )} `;
      }
      if (otherLineNames.length && memberLineNames.length) {
        dependentNames += `and `;
      }
      if (memberLineNames.length > 0) {
        dependentNames += `member${memberLineNames.length > 1 ? 's' : ''} ${formatAsNaturalLanguageList(
          memberLineNames,
          'and',
        )} `;
      }

      failBecause(`${dependentNames}depend${otherLineNames.length + memberLineNames.length > 1 ? '' : 's'} on it.`);
      return;
    }

    if (memberGeometries.some(references(referenceLine))) {
      failBecause(`it is referenced by a member's relative geometry.`);
      return;
    }

    dispatch(deselectEntriesBeingDeletedIfNecessary({ type: 'referenceLine', item: referenceLine }));

    const updatedComponent: Component = {
      ...component,
      referenceLines: component.referenceLines.filter((line) => line.guid !== referenceLine.guid),
    };

    dispatch(setComponent({ component: updatedComponent, componentDesignGuid: componentDesign.guid }));
  };

export const deleteUnusedReferenceLines = (): ThunkActionVoid => (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  const component = activeComponentDesign?.component;
  const referenceLines = component?.referenceLines;
  if (!activeComponentDesign || !component || !referenceLines || hasInvalidRelativeGeometry(component)) return;

  const usedReferenceLineGuids = getUsedReferenceLineGuids(component);
  const [usedReferenceLines, unusedReferenceLines] = partition(referenceLines, (line) =>
    usedReferenceLineGuids.has(line.guid),
  );

  const newComponent: Component = { ...component, referenceLines: usedReferenceLines };
  if (hasInvalidRelativeGeometry(newComponent)) {
    dispatch(enqueueError('Failed to delete unused reference lines.'));
    return;
  }

  dispatch(
    deselectEntriesBeingDeletedIfNecessary(unusedReferenceLines.map((line) => ({ type: 'referenceLine', item: line }))),
  );

  dispatch(setComponent({ componentDesignGuid: activeComponentDesign.guid, component: newComponent }));
};

/**
 * Gets the reference lines that are referenced by something (e.g., relative geometry, member lines, and standard
 * reference lines).
 */
function getUsedReferenceLineGuids(component: Component) {
  if (!component.referenceLines) throw new Error('Component does not have reference lines.');
  const relativeGeometries = getAllRelativeGeometries(component);

  // First we collect all the reference lines that are referenced by something that needs to be preserved.
  const topLevelReferenceLineGuidsToPreserve = new Set(
    [
      ...relativeGeometries.flatMap(getAllReferencedLineGuidsForRelativeGeometry),
      ...component.referenceLines.filter((line) => line.lineType === 'Member').map((line) => line.guid),
      ...standardReferenceLineKeys.map((key) => component[key]),
    ].filter(notNullish),
  );

  const usedReferenceLineGuids = new Set<string>();

  // Then we just find all the descendants using a depth-first search
  for (const referenceLineGuidToPreserve of topLevelReferenceLineGuidsToPreserve) {
    usedReferenceLineGuids.add(referenceLineGuidToPreserve);
    const referenceLineToPreserve = component.referenceLines.find((l) => l.guid === referenceLineGuidToPreserve);
    if (!referenceLineToPreserve) throw new Error('Unable to find reference line.');
    getAllDependenciesOfCalculatedLine(
      referenceLineToPreserve,
      component.referenceLines,
      relativeGeometries,
      usedReferenceLineGuids,
    );
  }

  return usedReferenceLineGuids;
}

// Broken out into a separate action for use by all three types of load settings.
function deleteLoadSettings(
  entry:
    SelectableEntryOfType<'manualLoad'> | SelectableEntryOfType<'movingLoad'> | SelectableEntryOfType<'parapetLoad'>,

  componentDesign: ComponentDesign,
): AsyncThunkActionVoid {
  return async (dispatch) => {
    if (!componentDesign.additionalLoads) return;

    const settingsProperty = ADDITIONAL_LOAD_PROPERTIES[entry.type];

    const oldSettings = componentDesign.additionalLoads[settingsProperty];

    const newSettings = oldSettings.filter((load) => load.guid !== entry.item.guid);

    const newAdditionalLoads = {
      ...componentDesign.additionalLoads,
      [settingsProperty]: newSettings,
    };

    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));

    dispatch(
      setAdditionalLoads({
        additionalLoads: newAdditionalLoads,
        componentDesignGuid: componentDesign.guid,
      }),
    );
    await dispatch(generateLoadCasesOfActiveComponentDesign());
  };
}

const manageBuildingVisibilityBeforeDeletion =
  (buildingGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const visibleBuilding = getVisibleBuildingGuidOrNull(state);

    if (visibleBuilding == null || visibleBuilding !== buildingGuid) return;

    const otherBuildingGuids = without(Array.from(getAllBuildingGuids(state)), buildingGuid);
    if (otherBuildingGuids.length === 0) {
      dispatch(ghostObjectVisibilityEnabled());
      return;
    }

    const newActiveBuilding = pickSingleActiveBuildingGuid(otherBuildingGuids);

    const levelGuids = getAllLevelGuids(state);
    const insertions = getInsertionsWithPendingChanges(state);

    const levelsInTargetBuilding = uniq(
      insertions
        .filter((insertion) => levelGuids.has(insertion.objectGuid) && insertion.parentGuid === newActiveBuilding)
        .map((insertion) => insertion.objectGuid),
    );
    const newActiveLevel = pickSingleActiveLevelGuid(levelsInTargetBuilding);
    if (newActiveLevel == null) throw new Error('Unexpected building without a level during deletion.');

    dispatch(ensureLevelIsVisible(newActiveLevel, { makeActive: true, exclusive: true }));
  };

const deleteSinglyInsertedLayoutModelEntry =
  (entry: SelectableEntryOfType<'building' | 'level'>): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const projectHierarchyModificationEnabled = hasProjectHierarchyFeatureFlag(state);
    if (!projectHierarchyModificationEnabled)
      throw new Error(
        'User does not have permission to delete building/level, but was able to get to the action anyway.',
      );

    const allLevelGuids = getAllLevelGuids(state);
    const layoutModels = getLayoutModelsWithPendingChanges(state);
    const insertions = getInsertionsWithPendingChanges(state);

    const layoutModel = layoutModels[entry.item.guid];
    if (layoutModel == null || layoutModel.type !== entry.type) {
      throw new Error(`Unable to find ${entry.type} model to delete`);
    }

    const modelInsertion = insertions.find((insertion) => insertion.objectGuid === layoutModel.guid);
    if (modelInsertion == null) throw new Error(`Unable to find insertion of ${entry.type} to delete`);

    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));

    // buildings might still contain (empty) levels, which we want to delete along with the building
    if (entry.type === 'building') {
      const levelInsertionsInBuilding = insertions.filter(
        (insertion) => insertion.parentGuid === entry.item.guid && allLevelGuids.has(insertion.objectGuid),
      );

      if (levelInsertionsInBuilding.length > 0) {
        dispatch(
          deselectEntriesBeingDeletedIfNecessary(
            uniq(levelInsertionsInBuilding.map((insertion) => insertion.objectGuid)).map((levelGuid) => ({
              type: 'level',
              item: layoutModels[levelGuid],
            })),
          ),
        );

        for (const levelInsertion of levelInsertionsInBuilding) {
          dispatch(
            insertionSetQuantity({
              insertion: levelInsertion,
              change: -1,
            }),
          );
        }
      }
    }

    dispatch(
      insertionSetQuantity({
        insertion: modelInsertion,
        change: -1, // assume single insertion, which should always be the case for buildings and levels
      }),
    );

    await dispatch(saveAllProjectChanges());
  };

//////////////////////////////////////////////////////////
// Configuration for the universal deletion actions. /////
//////////////////////////////////////////////////////////

allowDeleting('analogJoint', (requires) => {
  const entries = requires.entriesToDelete();
  const componentDesign = requires.activeComponentDesign();

  const analogModel = requires.uniqueRestriction(() => {
    if (componentDesign.analogModel == null) {
      throw new PreventDeletion('Cannot delete analog joint because the component design has no analog model.');
    }

    return componentDesign.analogModel;
  });

  const jointsToDelete = new Set(entries.map((entry) => entry.item.guid));

  return (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));

    dispatch(
      setAnalogModel({
        componentDesignGuid: componentDesign.guid,

        analogModel: {
          ...analogModel,

          analogJoints: analogModel.analogJoints.filter((joint) => !jointsToDelete.has(joint.guid)),
          analogLinks: analogModel.analogLinks.filter(
            (link) => !jointsToDelete.has(link.iJoint.guid) && !jointsToDelete.has(link.jJoint.guid),
          ),
        },
      }),
    );
  };
});

allowDeleting('analogLink', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return (dispatch) => {
    if (!componentDesign || !componentDesign.analogModel) return;

    const index = componentDesign.analogModel.analogLinks.findIndex(
      (analogLink) => analogLink.guid === entry.item.guid,
    );
    if (index === -1) return;

    const newAnalogLinks = [...componentDesign.analogModel.analogLinks];
    newAnalogLinks.splice(index, 1);
    let removeIJoint = false;
    let removeJJoint = false;
    if (
      newAnalogLinks.filter(
        (analogLink) =>
          analogLink.iJoint.guid === entry.item.iJoint.guid || analogLink.jJoint.guid === entry.item.iJoint.guid,
      ).length === 0
    ) {
      removeIJoint = true;
    }
    if (
      newAnalogLinks.filter(
        (analogLink) =>
          analogLink.iJoint.guid === entry.item.jJoint.guid || analogLink.jJoint.guid === entry.item.jJoint.guid,
      ).length === 0
    ) {
      removeJJoint = true;
    }
    const newAnalogJoints = componentDesign.analogModel.analogJoints.filter((analogJoint) => {
      if (removeIJoint && analogJoint.guid === entry.item.iJoint.guid) return false;
      if (removeJJoint && analogJoint.guid === entry.item.jJoint.guid) return false;
      return true;
    });
    const updatedAnalogModel = {
      ...componentDesign.analogModel,
      analogLinks: newAnalogLinks,
      analogJoints: newAnalogJoints,
    };

    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));
    dispatch(setAnalogModel({ analogModel: updatedAnalogModel, componentDesignGuid: componentDesign.guid }));
  };
});

allowDeleting('bearing', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch, getState) => {
    const bearingOccursInJackGirderConnection = Object.values(getState().jackGirderConnections.data).some(
      (connection) => connection.jackBearingGuid === entry.item.guid,
    );

    if (bearingOccursInJackGirderConnection) {
      dispatch(showDeleteCarriedBearingConfirmationDialog(componentDesign, entry.item));
    } else {
      await dispatch(deleteBearing(componentDesign, entry.item));
    }
  };
});

const bearingEnvelopeDeletionHandler: DeletionActionCreator<'beam' | 'wall'> = (requires) => {
  const entries = requires.entriesToDelete();

  requires.uniqueRestriction(({ openContext }) => {
    if (openContext?.type !== 'project') {
      const term = getBearingEnvelopeUserVisibleTerm(entries[0]?.item.bearingType, {
        plural: entries.length !== 1,
        capitalize: false,
      });
      throw new PreventDeletion(`Can't delete ${term} when not in a project.`);
    }
  });

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));

    const bearingsToDelete = entries.map(({ item }) => item);
    await dispatch(disassociatePlanesFromBearings(bearingsToDelete));
    await dispatch(disassociateFeaturePatternContainersFromBearings(bearingsToDelete));

    const guidsToDelete = new Set(bearingsToDelete.map(({ guid }) => guid));
    dispatch(layoutSlice.actions.bearingEnvelopesDeleted(Array.from(guidsToDelete)));

    await dispatch(saveBearingEnvelopes());
  };
};

allowDeleting('beam', bearingEnvelopeDeletionHandler);
allowDeleting('wall', bearingEnvelopeDeletionHandler);

allowDeleting('brace', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    await dispatch(deleteMembers(componentDesign, [entry.item], { isBrace: true }));
  };
});

allowDeleting('building', (requires) => {
  const entry = requires.singleEntryToDelete();

  requires.uniqueRestriction(({ layoutModelOccupancyReport: { occupiedModels } }) => {
    if (!occupiedModels.has(entry.item.guid)) throw new PreventDeletion('Building is not fully loaded.');
    if (occupiedModels.get(entry.item.guid)) throw new PreventDeletion('Building is not empty.');
  });

  return async (dispatch) => {
    dispatch(manageBuildingVisibilityBeforeDeletion(entry.item.guid));
    dispatch(deleteSinglyInsertedLayoutModelEntry(entry));
  };
});

allowDeleting('ceilingPlane', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch, getState) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));

    dispatch(layoutSlice.actions.ceilingPlanesDeleted(entries.map((entry) => entry.item.guid)));

    // Save truss envelopes first to remove any references to the deleted ceiling planes. Hopefully making two separate
    // network requests will go away when we start saving the entire layout at once.
    await dispatch(saveTrussEnvelopes(Object.values(getState().layout.trussEnvelopes)));
    await dispatch(saveCeilingPlanes());
  };
});

allowDeleting('ceilingTray', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));
    dispatch(layoutSlice.actions.ceilingTraysDeleted(entries.map((entry) => entry.item.guid)));
    await dispatch(saveCeilingTrays());
  };
});

allowDeleting('componentDesign', (requires) => {
  const entries = requires.entriesToDelete();

  requires.uniqueRestriction(({ insertingComponentDesignOrRewebbing }) => {
    if (insertingComponentDesignOrRewebbing) {
      throw new PreventDeletion("Can't delete component design while inserting a component design or rewebbing.");
    }
  });

  return (dispatch) => {
    dispatch(showDeleteComponentConfirmationDialog(entries.map((entry) => entry.item.guid)));
  };
});

allowDeleting('featureArea', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));
    dispatch(layoutSlice.actions.featureAreasDeleted(entries.map((entry) => entry.item.guid)));
    await dispatch(saveFeaturePatternContainers());
  };
});

allowDeleting('featurePatternContainer', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch, getState) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));

    const { openContext } = getState().application;
    if (openContext?.type !== 'project') {
      dispatch(enqueueError("Can't delete feature pattern containers when not in a project."));
      return;
    }

    const featurePatternContainers = entries.map(({ item }) => item);
    await dispatch(
      deleteFeaturePatternContainersAndAssociatedTrussEnvelopes(openContext.projectGuid, featurePatternContainers),
    );
  };
});

allowDeleting('gusset', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));
    dispatch(
      setComponent({
        component: {
          ...componentDesign.component,
          gussets: componentDesign.component.gussets.filter((gusset) => gusset.guid !== entry.item.guid),
        },
        componentDesignGuid: componentDesign.guid,
        skipInvalidation: true,
      }),
    );
  };
});

allowDeleting('hanger', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));
    await dispatch(deleteHangers(componentDesign, [entry.item.guid]));
  };
});

allowDeleting('jackGirderConnection', (requires) => {
  requires.activeComponentDesign();
  const entry = requires.singleEntryToDelete();

  return async (dispatch) => {
    await dispatch(deleteJackGirderConnections(entry.item));
  };
});

allowDeleting('workLine', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch, getState) => {
    const guids = entries.map((entry) => entry.item.guid);
    const affectedLevelGuids = getLevelGuidsAffectedByWorkLineEdit(getState(), guids, []);

    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));
    dispatch(layoutSlice.actions.workLinesDeleted({ guids, affectedLevelGuids }));
    await dispatch(saveWorkLines());
  };
});

allowDeleting('snappingUnderlay', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));
    dispatch(layoutSlice.actions.snappingUnderlaysDeleted(entries.map((entry) => entry.item.guid)));
    await dispatch(saveSnappingUnderlays());
  };
});

allowDeleting('level', (requires) => {
  const entry = requires.singleEntryToDelete();

  requires.uniqueRestriction(({ layoutModelOccupancyReport: { occupiedModels, isolatedLevels } }) => {
    if (!occupiedModels.has(entry.item.guid)) throw new PreventDeletion('Level is not fully loaded.');

    if (occupiedModels.get(entry.item.guid)) throw new PreventDeletion('Level is not empty.');
    if (isolatedLevels.has(entry.item.guid)) throw new PreventDeletion('Level is last remaining in its building.');
  });

  return (dispatch) => {
    dispatch(hideLevelByGuid(entry.item.guid));
    dispatch(deleteSinglyInsertedLayoutModelEntry(entry));
  };
});

allowDeleting('loadCase', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    if (componentDesign.additionalLoads == null) {
      console.error('Unable to delete load case because componentDesign additionalLoads is null');
      return;
    }

    const newManualLoadCaseSettings = componentDesign.additionalLoads.manualLoadCases.filter(
      (manualLoadCase) => manualLoadCase.guid !== entry.item.guid,
    );
    if (newManualLoadCaseSettings.length === componentDesign.additionalLoads.manualLoadCases.length) {
      dispatch(enqueueWarning('You can only delete manual load cases'));
      return;
    }

    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));

    const updatedAdditionalLoads = {
      ...componentDesign.additionalLoads,
      manualLoadCases: newManualLoadCaseSettings,
    };
    dispatch(
      setAdditionalLoads({
        additionalLoads: updatedAdditionalLoads,
        componentDesignGuid: componentDesign.guid,
      }),
    );
    dispatch(cycleVisibilityOfItem(entry));
    await dispatch(generateLoadCasesOfActiveComponentDesign());
  };
});

allowDeleting('manualLoad', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));
    await dispatch(deleteLoadSettings(entry, componentDesign));
  };
});

allowDeleting('member', (requires) => {
  const members = requires.entriesToDelete().map((entry) => entry.item);
  const componentDesign = requires.activeComponentDesign();

  return deleteMembers(componentDesign, members, { isBrace: false });
});

allowDeleting('movingLoad', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    await dispatch(deleteLoadSettings(entry, componentDesign));
  };
});

allowDeleting('parapetLoad', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    await dispatch(deleteLoadSettings(entry, componentDesign));
  };
});

allowDeleting('platePair', (requires) => {
  const entries = requires.entriesToDelete();
  const componentDesign = requires.activeComponentDesign();

  const platePairsToDelete = new Set(entries.map((entry) => entry.item.guid));

  return (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));
    dispatch(
      setPlatePairs({
        componentDesignGuid: componentDesign.guid,
        platePairs: componentDesign.component.platePairs.filter((pair) => !platePairsToDelete.has(pair.guid)),
      }),
    );
  };
});

allowDeleting('referenceLine', (requires) => {
  const { component } = requires.activeComponentDesign();
  const { item: referenceLine } = requires.singleEntryToDelete();

  requires.uniqueRestriction(() => {
    if (standardReferenceLineKeys.some((key) => component[key] === referenceLine.guid)) {
      throw new PreventDeletion('Cannot delete standard reference line.');
    }
  });

  return async (dispatch) => {
    await dispatch(deleteReferenceLine(referenceLine));
  };
});

allowDeleting('roofContainer', (requires) => {
  const entry = requires.singleEntryToDelete();

  return async (dispatch, getState) => {
    const state = getState();
    const roofContainer = entry.item;

    const { openContext } = state.application;
    if (openContext?.type !== 'project') {
      dispatch(enqueueError("Can't delete roof containers when not in a project."));
      return;
    }

    const trussEnvelopesThatReferenceRoofContainer = Object.values(state.layout.trussEnvelopes).filter(
      (trussEnvelope) => trussEnvelope.roofContainerGuid === roofContainer.guid,
    );
    if (trussEnvelopesThatReferenceRoofContainer.length !== 0) {
      dispatch(showDeleteRoofContainerConfirmationDialog(openContext.projectGuid, roofContainer));
      return;
    }

    await dispatch(deleteRoofContainerAndAssociatedTrussEnvelopes(openContext.projectGuid, roofContainer));
  };
});

allowDeleting('roofPlane', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch, getState) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));

    dispatch(layoutSlice.actions.roofPlanesDeleted(entries.map((entry) => entry.item.guid)));

    // Save truss envelopes first to remove any references to the deleted roof planes. Hopefully making two separate
    // network requests will go away when we start saving the entire layout at once.
    await dispatch(saveTrussEnvelopes(Object.values(getState().layout.trussEnvelopes)));
    await dispatch(saveRoofPlanes());
  };
});

allowDeleting('splice', (requires) => {
  const entry = requires.singleEntryToDelete();
  const componentDesign = requires.activeComponentDesign();

  return async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage(`Deleting splice ${entry.item.name}...`));
    try {
      const newComponentDesign = await designService.componentDesigns.postDeleteSplice(
        componentDesign,
        entry.item.neighbor1Guid,
        entry.item.neighbor2Guid,
      );

      dispatch(deselectEntriesBeingDeletedIfNecessary(entry));
      dispatch(setUnsavedComponentDesignFromServer(newComponentDesign));
    } catch (error) {
      dispatch(enqueueError(`Unable to delete ${entry.item.name}. Try again, or contact Paragon Customer Support.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
});

allowDeleting('trussEnvelope', (requires) => {
  const entries = requires.entriesToDelete();

  const projectGuid = requires.uniqueRestriction(({ openContext }) => {
    if (openContext?.type !== 'project') {
      throw new PreventDeletion("Can't delete truss envelope when not in a project.");
    }

    return openContext.projectGuid;
  });

  return deleteTrussEnvelopes(entries, projectGuid, { withSpinnerMessage: true });
});

allowDeleting('trussGroup', (requires) => {
  const entries = requires.entriesToDelete();

  const projectGuid = requires.uniqueRestriction(({ openContext }) => {
    if (openContext?.type !== 'project') {
      throw new PreventDeletion("Can't delete truss group when not in a project.");
    }

    return openContext.projectGuid;
  });

  return async (dispatch, getState) => {
    const trussGroupGuidsBeingDeleted = entries.map((entry) => entry.item.guid);
    const parentTrussGroupGuids = Object.values(getState().trussGroups)
      .map((trussGroup) => trussGroup.parentGuid)
      .filter(notNullish);
    const [trussGroupGuidsBeingDeletedWhichAreParents, trussGroupGuidsBeingDeletedWhichAreNotParents] = partition(
      trussGroupGuidsBeingDeleted,
      (guid) => parentTrussGroupGuids.includes(guid),
    );

    if (trussGroupGuidsBeingDeletedWhichAreParents.length > 0) {
      const trussGroupsKeyedByParentGuid = groupByWithoutNullishKeys(
        Object.values(getState().trussGroups),
        (trussGroup) => trussGroup.parentGuid,
      );

      const childTrussGroupsWhichWillBeCascadedDeleted: string[] = [];
      for (const trussGroupGuidToDelete of trussGroupGuidsBeingDeletedWhichAreParents) {
        let children = trussGroupsKeyedByParentGuid[trussGroupGuidToDelete] ?? [];
        while (children.length !== 0) {
          childTrussGroupsWhichWillBeCascadedDeleted.push(...children.map((child) => child.guid));
          children = children.flatMap((child) => trussGroupsKeyedByParentGuid[child.guid] ?? []);
        }
      }

      dispatch(
        showDeleteTrussGroupsConfirmationDialog(
          trussGroupGuidsBeingDeletedWhichAreParents,
          childTrussGroupsWhichWillBeCascadedDeleted,
        ),
      );
    }

    if (trussGroupGuidsBeingDeletedWhichAreNotParents.length > 0) {
      await dispatch(deleteTrussGroups(projectGuid, trussGroupGuidsBeingDeletedWhichAreNotParents));
    }
  };
});

allowDeleting('webJoint', (requires) => {
  const entry = requires.singleEntryToDelete();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entry));
    await dispatch(deleteFixedWebJoint(entry.item));
  };
});

allowDeleting('webSegment', (requires) => {
  const entries = requires.entriesToDelete();

  return async (dispatch) => {
    dispatch(deselectEntriesBeingDeletedIfNecessary(entries));
    await dispatch(deleteWebSegmentsAndRewebTruss(entries.map(({ item }) => item.guid)));
  };
});

////////////////////////////////////////////////////////
// Infrastructure for the universal deletion actions. //
////////////////////////////////////////////////////////

function allowDeleting<T extends SelectableItemType>(
  itemType: T,
  requirementsCallback: (typeof deletionActionCreators)[T],
) {
  deletionActionCreators[itemType] = requirementsCallback;
}

interface DeletionData {
  ongoingTasks: boolean;
  insertingOrPicking: boolean;
  insertingComponentDesignOrRewebbing: boolean;

  openContext: ClientContext | null;

  activeComponentDesign: ComponentDesign | null;

  isLayoutModeViewOnly: boolean;

  layoutModelOccupancyReport: LayoutModelOccupancyReport;
}

interface DeletionActionFactoryProps {
  data: DeletionData;
  selectedEntryFactory: SelectedEntryFactory;
}

export const getDeletionProps = createSelector(
  [
    getSelectedEntryFactory,
    areAnySpinnersActive,
    (state: RootState) =>
      (!!state.application.insertingObjectInfo && state.application.insertingObjectInfo?.type !== 'componentDesign') ||
      isPickingActive(state),
    (state: RootState) =>
      state.application.insertingObjectInfo?.type === 'componentDesign' || !!state.application.rewebbingInfo,
    (state: RootState) => state.application.openContext,
    getActiveComponentDesign,
    isLayoutModeViewOnly,
    getLayoutModelOccupancyReport,
  ],

  (
    selectedEntryFactory,
    ongoingTasks,
    insertingOrPicking,
    insertingComponentDesignOrRewebbing,
    openContext,
    activeComponentDesign,
    isLayoutModeViewOnly,
    layoutModelOccupancyReport,
  ): DeletionActionFactoryProps => ({
    selectedEntryFactory,
    data: {
      ongoingTasks,
      insertingOrPicking,
      insertingComponentDesignOrRewebbing,
      activeComponentDesign,
      openContext,
      isLayoutModeViewOnly,
      layoutModelOccupancyReport,
    },
  }),
);

export class DeletionActionFactory {
  private readonly props: DeletionActionFactoryProps;
  private readonly thingsToDelete: (SelectedItemIdentifier | SelectableItemEntry)[] = [];

  private readonly lookUpItemEntry: SelectedEntryFactory;

  constructor(itemsToDelete: (SelectedItemIdentifier | SelectableItemEntry)[], props: DeletionActionFactoryProps) {
    this.props = props;
    this.thingsToDelete = itemsToDelete;

    this.lookUpItemEntry = props.selectedEntryFactory;
  }

  isDeletionFeasible(): boolean {
    return this.getDeletionFeasibility().deletionFeasible;
  }

  getDeletionFeasibility():
    { readonly deletionFeasible: true } | { readonly deletionFeasible: false; readonly failureReason: string } {
    const result = this.getDeletionActionOrFailureReason();
    return result instanceof Function ? { deletionFeasible: true } : { deletionFeasible: false, failureReason: result };
  }

  getDeletionAction(): AsyncThunkActionVoid {
    return async (dispatch) => {
      const action = this.getDeletionActionOrFailureReason();

      if (action instanceof Function) {
        dispatch(DeletionActionFactory.universalPreDeletionAction());
        await dispatch(action);
      } else {
        throw new Error(action);
      }
    };
  }

  private static universalPreDeletionAction(): ThunkActionVoid {
    return (dispatch) => dispatch(clearRightClickedItems());
  }

  private getDeletionActionOrFailureReason(): DeletionAction | string {
    if (this.thingsToDelete.length === 0) {
      return 'No items slated for deletion.';
    }

    if (this.props.data.ongoingTasks) {
      return 'Cannot perform deletion while other tasks are ongoing.';
    }

    if (this.props.data.insertingOrPicking) {
      return 'Cannot perform deletion while inserting or picking.';
    }

    const { type } = this.thingsToDelete[0];
    if (this.thingsToDelete.some((entry) => entry.type !== type)) {
      return `All entries must be of the same type when performing bulk deletion.`;
    }

    if (isConsideredLayoutItem(type) && this.props.data.isLayoutModeViewOnly) {
      return 'Layout entries cannot be deleted when the layout is read-only.';
    }

    const actionCreator = deletionActionCreators[type] as DeletionActionCreator<typeof type> | undefined;
    if (actionCreator == null) {
      return `No deletion action creator found for ${type}.`;
    }

    // cast is needed because typescript will not recognize that the check below ensures there are no null entries
    const entries = this.convertAndGetEntriesToDelete() as SelectableEntryOfType<typeof type>[];
    if (entries.some((entry) => entry == null)) {
      return 'Some entries to delete could not be found.';
    }

    try {
      return actionCreator(new RequirementsProvider(entries, this.props.data));
    } catch (e) {
      if (e instanceof PreventDeletion) {
        return e.reason;
      }

      throw e;
    }
  }

  private convertAndGetEntriesToDelete() {
    return this.thingsToDelete.map((thing) => (isEntry(thing) ? thing : this.lookUpItemEntry(thing)));
  }
}

class PreventDeletion {
  constructor(public readonly reason: string) {}
}

class RequirementsProvider<T extends SelectableItemType> {
  private readonly entries: SelectableEntryOfType<T>[];
  private readonly data: DeletionData;

  constructor(entries: SelectableEntryOfType<T>[], data: DeletionData) {
    this.entries = entries;
    this.data = data;
  }

  private get type() {
    return this.entries[0].type;
  }

  activeComponentDesign() {
    if (this.data.activeComponentDesign == null) {
      throw new PreventDeletion(`An active component design is required for ${this.type} deletion.`);
    }

    return this.data.activeComponentDesign;
  }

  singleEntryToDelete() {
    if (this.entries.length > 1) {
      throw new PreventDeletion(`Multiple selection is not supported for ${this.type} deletion.`);
    }

    return this.entries[0];
  }

  entriesToDelete() {
    return this.entries;
  }

  uniqueRestriction<T>(callback: (data: DeletionData) => T): T {
    return callback(this.data);
  }
}
